import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  SEVERITY_BG,
  ScoreBar,
  SeverityBar,
  type Severity,
  type Thresholds,
} from '@/components/ui/charts';
import { CitationChip } from '@/components/ui/citation-chip';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceButton } from '@/components/ui/workspace/button';
import {
  useCanAdjustAssessment,
  useRateDomainMutation,
  type HrCriterion,
  type HrDomain,
  type HrOpenByDomain,
} from '@/lib/api/health-readiness';
import type { DomainRating } from '@/lib/api/health-domains';
import {
  extractFindingPayloadString,
  useFindingListQuery,
  type FindingListItem,
} from '@/lib/api/health-findings';
import { useCrossModuleReferencesQuery } from '@/lib/api/readiness-xref';
import { cn } from '@/lib/utils/cn';
import { formatDayMonth } from '@/lib/utils/format';
import { BudgetBlock } from './budget-block';
import { ScheduleBlock } from './schedule-block';
import {
  AI_TEXT,
  AiBox,
  AiMarker,
  CARD_LINK,
  DetailCard,
  Meta,
  OFORM,
  P_LABEL,
  RecheckButton,
  TEXTAREA,
  useRecheck,
} from './detail-parts';
import { findingAgeDays, isNewerThan, ratingMode, whoWhen } from './detail-model';
import { POSTURE_LABEL, formatPercent, plural, sameDomainName } from './model';
import { useHrNavigate } from './navigation';

/**
 * Domain detail (T-0243 / E22-F02; prototype `domainDetail()`,
 * `aiDomainBox()`, `ratingForm()`), top to bottom:
 *
 *   - the name, its rating chip, who rated it, Re-check (leads) and New finding;
 *   - the rating: Green / Amber / Red with the AI's reading marked; picking
 *     one opens the rating form with it, and the reason is required;
 *   - the AI reading with Confirm / Rate differently (both send the
 *     proposal id, so it leaves the review queue);
 *   - the rating at each snapshot, then today (`domains[].history`);
 *   - Schedule and Budget carry their milestones and spend;
 *   - the open findings in this domain (the API's counts by severity, then
 *     the list with each one's age) and the go-live criteria that read
 *     this rating, each with its score bar.
 *
 * Rating goes through the existing assessment lifecycle
 * (`useRateDomainMutation`). Nothing here adds anything up.
 */
export interface DomainPaneProps {
  engagementId: string;
  domain: HrDomain;
  /** Every criterion on the list, to name the ones that read this domain. */
  criteria: HrCriterion[];
  thresholds: Thresholds | null;
  /** The domain's open findings by severity, from the composite; null without a row. */
  openFindings: HrOpenByDomain | null;
}

const SCALE: Array<{ status: Exclude<PostureStatus, 'unassessed'>; rating: DomainRating }> = [
  { status: 'healthy', rating: 'healthy' },
  { status: 'at-risk', rating: 'at_risk' },
  { status: 'critical', rating: 'critical' },
];

const STATUS_OF: Record<DomainRating, PostureStatus> = {
  healthy: 'healthy',
  at_risk: 'at-risk',
  critical: 'critical',
};

const SCALE_ON: Record<Exclude<PostureStatus, 'unassessed'>, string> = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
};

/** The severity ramp's fill for a finding's severity; anything else (info) is plain ink. */
const SEVERITY_DOT: Record<string, string> = SEVERITY_BG;

interface RatingDraft {
  rating: DomainRating;
  why: string;
  /** Opened from "Confirm …": the AI's reading with its reason. */
  confirm: boolean;
}

export function DomainPane({
  engagementId,
  domain,
  criteria,
  thresholds,
  openFindings,
}: DomainPaneProps) {
  const go = useHrNavigate(engagementId);
  const canLead = useCanAdjustAssessment();
  const recheck = useRecheck(engagementId, 'domain');
  const [draft, setDraft] = useState<RatingDraft | null>(null);
  const rate = useRateDomainMutation(engagementId);
  const domainRefs = useCrossModuleReferencesQuery(engagementId, {
    source_entity_kind: 'domain_rating',
  });

  // The criteria that read this rating: the composite's own lists, plus the
  // live cross-module references of kind domain_rating that point at this
  // domain or its latest committed assessment.
  const reading = useMemo(() => {
    const ids = new Set<string>(domain.readByCriteria);
    for (const c of criteria) if (c.readsDomainIds.includes(domain.id)) ids.add(c.id);
    for (const ref of domainRefs.data?.items ?? []) {
      if (
        ref.source_entity_id === domain.id ||
        (domain.latestAssessmentId !== null && ref.source_entity_id === domain.latestAssessmentId)
      ) {
        ids.add(ref.criterion_id);
      }
    }
    return criteria.filter((c) => ids.has(c.id));
  }, [criteria, domain, domainRefs.data]);

  const proposal = domain.aiProposal;
  const open = (next: RatingDraft) => {
    rate.reset();
    setDraft(next);
  };
  const pick = (rating: DomainRating) => open({ rating, why: draft?.why ?? '', confirm: false });
  const save = (why: string) => {
    if (!draft) return;
    rate.mutate(
      {
        domainId: domain.id,
        inProgressAssessmentId: domain.inProgressAssessmentId,
        rating: draft.rating,
        narrative: why,
        // Both answers carry the proposal, so it leaves the review queue:
        // the AI's rating accepts it, another records it as overridden.
        aiProposalId: proposal?.proposalId ?? null,
        mode: ratingMode(draft.rating, proposal),
      },
      { onSuccess: () => setDraft(null) },
    );
  };

  const findingsFilter = (severity?: Severity) =>
    go({ tab: 'findings', filters: { domain: domain.id, severity } });
  const label = POSTURE_LABEL[domain.status];

  return (
    <div className="flex flex-col gap-4" data-testid="domain-pane">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="t-h1 flex flex-wrap items-center gap-2.5">
            {domain.name}
            <PostureChip status={domain.status} dot aria-label={`Rating: ${label}`}>
              {label}
            </PostureChip>
          </h1>
          <div className="text-meta text-ink-500">
            {whoWhen(domain.ratedAt, domain.ratedBy)} ·{' '}
            {plural(domain.openFindings, 'open finding', 'open findings')} · read by{' '}
            {plural(reading.length, 'go-live criterion', 'go-live criteria')}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canLead ? (
            <RecheckButton
              what={domain.name}
              pending={recheck.pending}
              onClick={() => recheck.request(domain.id)}
            />
          ) : null}
          <WorkspaceButton variant="secondary" onClick={() => findingsFilter()}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            New finding
          </WorkspaceButton>
        </div>
      </div>
      {recheck.askedFor === domain.id ? (
        <p role="status" className="text-meta text-ink-500">
          Re-check asked for: the AI reads every piece of evidence for {domain.name} again. Its
          reading shows here when it is done.
        </p>
      ) : null}
      {recheck.error ? <ErrorMessage error={recheck.error} /> : null}

      <section
        aria-label="Rating"
        className="grid gap-6 rounded-card border border-ink-100 bg-paper-0 p-4 md:grid-cols-[300px_1fr]"
      >
        <div>
          <div className={P_LABEL}>Rating</div>
          <div role="group" aria-label="Rate this domain" className="mt-2 flex gap-1">
            {SCALE.map(({ status, rating }) => {
              const on = (draft ? draft.rating : domain.rating) === rating;
              const isAi = proposal?.rating === rating;
              return (
                <button
                  key={status}
                  type="button"
                  aria-pressed={on}
                  aria-label={`${POSTURE_LABEL[status]}${isAi ? ', the AI reading' : ''}`}
                  onClick={() => pick(rating)}
                  className={cn(
                    'relative flex h-[52px] flex-1 flex-col items-center justify-center gap-1 rounded-card border text-body-sm font-semibold transition-colors motion-reduce:transition-none',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                    on
                      ? cn('border-transparent text-paper-0', SCALE_ON[status])
                      : 'border-ink-100 bg-paper-50 text-ink-500 hover:border-ink-300',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'h-3.5 w-3.5 rounded-full border-2',
                      on
                        ? 'border-paper-0 bg-paper-0 shadow-[0_0_0_4px_rgba(255,255,255,0.25)]'
                        : 'border-current opacity-60',
                    )}
                  />
                  {POSTURE_LABEL[status]}
                  {isAi ? (
                    <span aria-hidden="true" className="absolute -right-1 -top-2">
                      <AiMarker />
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="mt-2 text-meta text-ink-500">
            Pick a rating to change it; you say why.
          </div>
        </div>
        <div>
          <div className={P_LABEL}>Why</div>
          {domain.rationale ? (
            <>
              <p className="mb-1.5 mt-1 border-l-[3px] border-ink-100 py-0.5 pl-3.5 text-body text-ink-900">
                {domain.rationale}
              </p>
              <div className="text-meta text-ink-500">
                {whoWhen(domain.ratedAt, domain.ratedBy)}
              </div>
            </>
          ) : (
            <p className="mt-1 border-l-[3px] border-ink-100 py-0.5 pl-3.5 text-body text-ink-500">
              Not rated yet.
              {proposal
                ? ' The AI has a reading below.'
                : ' The AI proposes a reading when a document speaks to this domain.'}
            </p>
          )}
        </div>
      </section>

      {draft ? (
        <RatingForm
          name={domain.name}
          draft={draft}
          aiRating={proposal?.rating ?? null}
          onChange={setDraft}
          onSave={save}
          onCancel={() => setDraft(null)}
          pending={rate.isPending}
          error={rate.error}
        />
      ) : proposal ? (
        <AiBox
          confidence={proposal.confidence}
          isNew={isNewerThan(proposal.createdAt, domain.ratedAt)}
        >
          <p className={AI_TEXT}>
            <strong>{POSTURE_LABEL[proposal.status]}.</strong>{' '}
            {proposal.reasoning ?? 'No reasoning was recorded with this reading.'}
          </p>
          {proposal.citations.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {proposal.citations.map((c) => (
                <CitationChip key={c.id} cite={c} />
              ))}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <WorkspaceButton
              variant="primary"
              onClick={() =>
                open({ rating: proposal.rating, why: proposal.reasoning ?? '', confirm: true })
              }
            >
              Confirm {POSTURE_LABEL[proposal.status]}
            </WorkspaceButton>
            <WorkspaceButton
              variant="secondary"
              onClick={() =>
                open({ rating: domain.rating ?? proposal.rating, why: '', confirm: false })
              }
            >
              Rate differently…
            </WorkspaceButton>
            <span className="text-meta text-ink-500">
              The rating is yours; the reading cites the documents it used.
            </span>
          </div>
        </AiBox>
      ) : null}

      {domain.history.length > 0 ? (
        <DetailCard
          title="Rating over time"
          aside={<Meta>what each snapshot froze, then today</Meta>}
        >
          <ol className="flex items-end gap-0.5">
            {[
              ...domain.history.map((h) => ({
                key: h.snapshotId,
                status: h.status,
                day: formatDayMonth(h.capturedAt),
                now: false,
              })),
              { key: 'today', status: domain.status, day: 'Today', now: true },
            ].map((s, i, all) => (
              <li key={s.key} className="flex min-w-0 flex-1 flex-col gap-1.5">
                <span
                  className={cn(
                    'flex h-[30px] items-center justify-center text-[11px] font-semibold',
                    i === 0 && 'rounded-l-[6px]',
                    i === all.length - 1 && 'rounded-r-[6px]',
                    s.status === 'unassessed'
                      ? 'chart-unrated text-ink-500'
                      : cn('text-paper-0', SCALE_ON[s.status]),
                  )}
                >
                  {POSTURE_LABEL[s.status]}
                </span>
                <span
                  className={cn(
                    'text-center text-[11px]',
                    s.now ? 'font-semibold text-ink-900' : 'text-ink-500',
                  )}
                >
                  {s.day}
                </span>
              </li>
            ))}
          </ol>
        </DetailCard>
      ) : null}

      {sameDomainName(domain.name, 'Schedule') ? (
        <ScheduleBlock engagementId={engagementId} />
      ) : null}
      {sameDomainName(domain.name, 'Budget') ? <BudgetBlock engagementId={engagementId} /> : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <DetailCard
          title="Findings in this domain"
          aside={
            <button type="button" className={CARD_LINK} onClick={() => findingsFilter()}>
              All findings →
            </button>
          }
        >
          {openFindings && (openFindings.total ?? 0) > 0 ? (
            <SeverityBar
              counts={{
                critical: openFindings.critical,
                high: openFindings.high,
                medium: openFindings.medium,
                low: openFindings.low,
              }}
              label={domain.name}
              size="md"
              onSegmentSelect={(s) => findingsFilter(s)}
              className="mb-2.5"
            />
          ) : null}
          <DomainFindings
            engagementId={engagementId}
            domainId={domain.id}
            onOpen={() => findingsFilter()}
          />
        </DetailCard>
        <DetailCard title="Read by these criteria">
          {reading.length === 0 ? (
            <p className="text-meta text-ink-500">No go-live criterion reads this rating yet.</p>
          ) : (
            <ul className="flex flex-col">
              {reading.map((c) => (
                <li
                  key={c.id}
                  className="grid grid-cols-[1fr_120px_40px] items-center gap-2.5 border-t border-ink-100 py-2 text-body-sm first:border-t-0"
                >
                  <button
                    type="button"
                    onClick={() => go({ tab: 'criteria', id: c.id })}
                    className="truncate text-left text-ink-900 hover:underline hover:underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                  >
                    {c.name}
                  </button>
                  {thresholds ? (
                    <ScoreBar value={c.scorePercent} thresholds={thresholds} label={c.name} />
                  ) : (
                    <span />
                  )}
                  <span className="text-right font-mono text-meta font-medium text-ink-900">
                    {formatPercent(c.scorePercent)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </DetailCard>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The rating form (prototype `ratingForm()`): the picked rating and the
// reason, required ("the report quotes it").
// ---------------------------------------------------------------------------

function RatingForm({
  name,
  draft,
  aiRating,
  onChange,
  onSave,
  onCancel,
  pending,
  error,
}: {
  name: string;
  draft: RatingDraft;
  /** The AI proposal's rating, when one waits. */
  aiRating: DomainRating | null;
  onChange: (next: RatingDraft) => void;
  onSave: (why: string) => void;
  onCancel: () => void;
  pending: boolean;
  error: unknown;
}) {
  const [touched, setTouched] = useState(false);
  const why = draft.why.trim();
  const missing = why.length === 0;
  const title = draft.confirm ? 'Confirm the AI reading' : `Rate ${name}`;
  const status = STATUS_OF[draft.rating];
  return (
    <form
      aria-label={title}
      className={OFORM}
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (missing) return;
        onSave(why);
      }}
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <span className={P_LABEL}>{title}</span>
        <PostureChip status={status} dot aria-label={`Rating: ${POSTURE_LABEL[status]}`}>
          {POSTURE_LABEL[status]}
        </PostureChip>
      </div>
      <textarea
        aria-label="Why"
        value={draft.why}
        onChange={(e) => onChange({ ...draft, why: e.target.value })}
        onBlur={() => setTouched(true)}
        placeholder="Why, in a sentence or two the report can quote"
        rows={3}
        aria-invalid={touched && missing ? true : undefined}
        className={TEXTAREA}
      />
      {touched && missing ? (
        <p role="alert" className="text-meta text-posture-critical">
          Say why in a sentence; the report quotes it.
        </p>
      ) : null}
      {error ? <ErrorMessage error={error} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        <WorkspaceButton type="submit" variant="primary" disabled={pending}>
          {pending ? 'Saving…' : 'Save rating'}
        </WorkspaceButton>
        <WorkspaceButton variant="tertiary" onClick={onCancel} disabled={pending}>
          Cancel
        </WorkspaceButton>
        {aiRating !== null && draft.rating !== aiRating ? (
          <span className="text-meta text-ink-500">
            Recorded with your reason; the AI reading stays in the history.
          </span>
        ) : null}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// The open findings in this domain, each with its age.
// ---------------------------------------------------------------------------

function DomainFindings({
  engagementId,
  domainId,
  onOpen,
}: {
  engagementId: string;
  domainId: string;
  onOpen: () => void;
}) {
  const query = useFindingListQuery(engagementId, { health_domain_id: domainId, status: 'open' });
  const today = useMemo(() => new Date(), []);
  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 text-meta text-ink-500">
        <Spinner /> Loading findings…
      </div>
    );
  }
  if (query.error) return <ErrorMessage error={query.error} />;
  const rows: FindingListItem[] = query.data?.items ?? [];
  if (rows.length === 0) return <p className="text-meta text-ink-500">No open findings.</p>;
  return (
    <ul className="flex flex-col">
      {rows.map((f) => {
        const title = extractFindingPayloadString(f.payload, 'title') ?? '(untitled finding)';
        const severity = f.severity ?? 'none';
        return (
          <li
            key={f.id}
            className="grid grid-cols-[14px_1fr_auto] items-center gap-2.5 border-t border-ink-100 py-2 text-body-sm first:border-t-0"
          >
            <span
              title={severity}
              className={cn('h-2.5 w-2.5 rounded-[3px]', SEVERITY_DOT[severity] ?? 'bg-ink-300')}
            >
              <span className="sr-only">{severity}</span>
            </span>
            <button
              type="button"
              onClick={onOpen}
              className="truncate text-left text-ink-900 hover:underline hover:underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
            >
              {title}
            </button>
            <span className="text-meta text-ink-500">
              {f.created_at ? `${findingAgeDays(f.created_at, today)}d` : ''}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
